<template>
  <div
    v-if="visibleRows.length || editing"
    ref="rootEl"
    class="lp-journey"
  >
    <div
      v-if="headerQuarters.length"
      class="lp-journey__row lp-journey__row--header"
    >
      <div
        class="lp-journey__label"
        aria-hidden="true"
      />
      <div class="lp-journey__track lp-journey__track--header">
        <div
          v-for="(q, qi) in headerQuarters"
          :key="q.key"
          class="lp-journey__q-head"
          :class="{ 'lp-journey__q-head--first': qi === 0 }"
        >
          {{ q.label }}
        </div>
      </div>
    </div>

    <div
      v-for="row in visibleRows"
      :key="row.id"
      class="lp-journey__row"
      :class="{ 'lp-journey__row--editing': editing }"
    >
      <div class="lp-journey__label">
        <i
          :class="row.icon"
          aria-hidden="true"
        />
        {{ row.label }}
      </div>

      <div
        v-if="editing && row.manual"
        class="lp-journey__edit"
      >
        <label class="lp-journey__edit-field">
          <span>{{ t('fields.submissionPlan') }}</span>
          <DatePicker v-model="row.stage.submission_plan" />
        </label>
        <label class="lp-journey__edit-field">
          <span>{{ t('fields.submissionActual') }}</span>
          <DatePicker v-model="row.stage.submission_actual" />
        </label>
        <label class="lp-journey__edit-field">
          <span>{{ t('fields.approvalPlan') }}</span>
          <DatePicker v-model="row.stage.approval_plan" />
        </label>
        <label class="lp-journey__edit-field">
          <span>{{ t('fields.approvalActual') }}</span>
          <DatePicker v-model="row.stage.approval_actual" />
        </label>
      </div>

      <div
        v-else-if="row.miles.length"
        class="lp-journey__track"
      >
        <div
          v-for="(q, qi) in quartersForRow(row)"
          :key="q.key"
          class="lp-journey__chevron"
          :data-current="q.isCurrent ? 'true' : undefined"
          :class="{
            'lp-journey__chevron--first': qi === 0,
            'lp-journey__chevron--filled': q.filled,
            'lp-journey__chevron--empty': !q.filled,
          }"
          :style="chevronStyle(q)"
        >
          <div
            v-if="!q.filled"
            class="lp-journey__empty"
          >—</div>
          <div
            v-for="(m, mi) in cellMiles(row, q.key)"
            :key="mi"
            class="lp-journey__mile"
          >
            <i
              :class="m.icon"
              aria-hidden="true"
            />
            <span class="lp-journey__mile-label">{{ m.label }}:</span>
            <span class="lp-journey__mile-value">
              <i
                class="bi bi-calendar3"
                aria-hidden="true"
              />
              {{ m.value }}
            </span>
            <Tooltip
              inline
              placement="top"
              :hide-delay="100"
              :text="m.actual ? t('ap.actual') : t('ap.plan')"
            >
              <span
                class="lp-journey__ap"
                :class="m.actual ? 'lp-journey__ap--actual' : 'lp-journey__ap--plan'"
                :style="apBadgeStyle(m.actual)"
                :aria-label="m.actual ? t('ap.actual') : t('ap.plan')"
              >{{ m.actual ? 'A' : 'P' }}</span>
            </Tooltip>
          </div>
        </div>
      </div>

      <div
        v-else-if="editing && !row.manual"
        class="lp-journey__hint"
      >
        {{ t('regulatoryAuto') }}
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * Product Journey Timeline (тикет AS-2000).
 * Clinical / Market Access / Release — вручную.
 * Regulatory — из реестров/SMoH.
 * Просмотр: только строки с датами; при наличии дат — всегда 5 кварталов
 * (2 до текущего, текущий, 2 после); пустые кварталы — серым.
 * Кварталы — общая шапка сверху; цвет шевронов — по строке этапа
 * (одна строка — один цвет; пустые — пастель того же цвета).
 * Редактирование: все 4 строки.
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import DatePicker from '@/components/datepicker.vue'
import Tooltip from '@/components/tooltip.vue'
import { formatHomeDateDisplay } from '@/lib/dateInput'
import { journeyStageHasDates } from '@/lib/productCard'

const props = defineProps({
  /** Ручные этапы product_journey (reactive object). */
  journey: { type: Object, default: () => ({}) },
  /** Regulatory: дата подачи (план или факт). */
  submissionDate: { type: [String, Date, Number], default: null },
  submissionActual: { type: Boolean, default: false },
  approvalDate: { type: [String, Date, Number], default: null },
  approvalActual: { type: Boolean, default: false },
  editing: { type: Boolean, default: false },
})

const { t } = useI18n()
const rootEl = ref(null)

/** Палитра по строкам этапов: Clinical → Regulatory → Market Access → Release. */
const ROW_PALETTE = [
  { color: '#F0AB00', pastel: '#FADFA1' },
  { color: '#68D2DF', pastel: '#C3ECF2' },
  { color: '#003865', pastel: '#B2C7D9' },
  { color: '#3C1053', pastel: '#B09BBA' },
]

function toDate(value) {
  if (value == null || value === '') return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

function quarterIndex(d) {
  return d.getFullYear() * 4 + Math.floor(d.getMonth() / 3) + 1
}

function quarterMeta(idx) {
  const q = ((idx - 1) % 4) + 1
  const year = Math.floor((idx - 1) / 4)
  return { key: `q${idx}`, label: `Q${q} ${year}`, idx, year }
}

function fmt(value) {
  if (value == null || value === '') return ''
  return formatHomeDateDisplay(value, { empty: '' })
}

function mileFrom(value, actual, icon, label) {
  const text = fmt(value)
  if (!text) return null
  const d = toDate(value)
  return {
    icon,
    label,
    value: text,
    actual: Boolean(actual),
    qIdx: d ? quarterIndex(d) : null,
  }
}

function milesFromStage(stage, labels) {
  if (!stage) return []
  const out = []
  const subAct = mileFrom(
    stage.submission_actual,
    true,
    'bi bi-send',
    labels.submission
  )
  const subPlan = !subAct
    ? mileFrom(stage.submission_plan, false, 'bi bi-send', labels.submission)
    : null
  const apprAct = mileFrom(
    stage.approval_actual,
    true,
    'bi bi-check-circle-fill',
    labels.approval
  )
  const apprPlan = !apprAct
    ? mileFrom(stage.approval_plan, false, 'bi bi-check-circle-fill', labels.approval)
    : null
  for (const m of [subAct || subPlan, apprAct || apprPlan]) {
    if (m) out.push(m)
  }
  return out
}

const regulatoryMiles = computed(() => {
  const out = []
  const sub = mileFrom(
    props.submissionDate,
    props.submissionActual,
    'bi bi-send',
    t('miles.ruSubmission')
  )
  const appr = mileFrom(
    props.approvalDate,
    props.approvalActual,
    'bi bi-check-circle-fill',
    t('miles.ruApproval')
  )
  if (sub) out.push(sub)
  if (appr) out.push(appr)
  return out
})

const stageMeta = [
  {
    id: 'clinical',
    icon: 'bi bi-heart-pulse',
    labelKey: 'rows.clinical',
    manual: true,
    labels: { submission: 'miles.clinicalStart', approval: 'miles.clinicalEnd' },
  },
  {
    id: 'regulatory',
    icon: 'bi bi-file-earmark-medical',
    labelKey: 'rows.regulatory',
    manual: false,
  },
  {
    id: 'market_access',
    icon: 'bi bi-shop',
    labelKey: 'rows.marketAccess',
    manual: true,
    labels: { submission: 'miles.maStart', approval: 'miles.maEnd' },
  },
  {
    id: 'release',
    icon: 'bi bi-box-seam',
    labelKey: 'rows.release',
    manual: true,
    labels: { submission: 'miles.releaseStart', approval: 'miles.releaseEnd' },
  },
]

const allRows = computed(() => {
  return stageMeta.map((meta, rowIndex) => {
    let miles = []
    let stage = null
    if (meta.manual) {
      stage = props.journey?.[meta.id] || {}
      miles = milesFromStage(stage, {
        submission: t(meta.labels.submission),
        approval: t(meta.labels.approval),
      }).map(m => ({
        ...m,
        quarter: m.qIdx != null ? `q${m.qIdx}` : null,
      }))
    } else {
      miles = regulatoryMiles.value.map(m => ({
        ...m,
        quarter: m.qIdx != null ? `q${m.qIdx}` : null,
      }))
    }
    const pal = ROW_PALETTE[rowIndex % ROW_PALETTE.length]
    return {
      id: meta.id,
      label: t(meta.labelKey),
      icon: meta.icon,
      manual: meta.manual,
      stage,
      miles,
      color: pal.color,
      pastel: pal.pastel,
      hasDates: meta.manual
        ? journeyStageHasDates(stage)
        : miles.length > 0,
    }
  })
})

const visibleRows = computed(() => {
  if (props.editing) {
    return allRows.value
  }
  return allRows.value.filter(row => row.hasDates)
})

/**
 * Общая шкала из пяти кварталов вокруг текущего (без цвета — цвет у строки).
 *
 * @param {Array} miles Вехи с qIdx.
 * @returns {Array} Метаданные кварталов.
 */
function buildQuartersAroundCurrent(miles) {
  const idxs = miles.map(m => m.qIdx).filter(v => v != null)
  if (!idxs.length) return []
  const now = quarterIndex(new Date())
  const start = now - 2
  const end = now + 2
  const list = []
  for (let i = start; i <= end; i += 1) {
    const meta = quarterMeta(i)
    list.push({
      ...meta,
      filled: miles.some(m => m.qIdx === i),
      isCurrent: i === now,
    })
  }
  return list
}

/** Общая шапка кварталов для всех видимых строк с датами. */
const headerQuarters = computed(() => {
  const miles = visibleRows.value.flatMap(row => row.miles || [])
  return buildQuartersAroundCurrent(miles).map(q => ({
    ...q,
    filled: false,
  }))
})

/**
 * Кварталы строки = общая шапка; цвет берётся у строки этапа.
 *
 * @param {object} row Строка этапа.
 * @returns {Array} Ячейки шевронов.
 */
function quartersForRow(row) {
  const shared = headerQuarters.value
  if (!shared.length) return []
  const placed = (row.miles || []).filter(m => m.qIdx != null)
  if (!placed.length) return []
  return shared.map(q => ({
    ...q,
    color: row.color,
    pastel: row.pastel,
    filled: placed.some(m => m.qIdx === q.idx),
  }))
}

function cellMiles(row, quarterKey) {
  return (row.miles || []).filter(m => m.quarter === quarterKey)
}

function chevronStyle(q) {
  if (q.filled) {
    return {
      background: q.color,
      color: '#ffffff',
    }
  }
  return {
    background: q.pastel,
    color: '#8A94A6',
  }
}

function apBadgeStyle(actual) {
  const ink = actual ? '#1B5E20' : '#0033A0'
  return {
    color: ink,
    backgroundColor: '#ffffff',
    WebkitTextFillColor: ink,
    textShadow: 'none',
  }
}

async function scrollToCurrentYear() {
  await nextTick()
  const root = rootEl.value
  if (!root) return
  root.querySelectorAll('.lp-journey__track').forEach(track => {
    const current = track.querySelector('[data-current="true"]')
    if (!current) return
    const delta = current.getBoundingClientRect().left - track.getBoundingClientRect().left
    track.scrollLeft = Math.max(0, track.scrollLeft + delta - 12)
  })
}

watch(visibleRows, scrollToCurrentYear, { deep: true })
onMounted(scrollToCurrentYear)
</script>

<i18n>
{
  "ru": {
    "rows": {
      "clinical": "Clinical",
      "regulatory": "Regulatory",
      "marketAccess": "Market Access",
      "release": "Release"
    },
    "miles": {
      "ruSubmission": "RU submission",
      "ruApproval": "RU approval",
      "clinicalStart": "Start",
      "clinicalEnd": "End",
      "maStart": "Start",
      "maEnd": "End",
      "releaseStart": "Start",
      "releaseEnd": "End"
    },
    "fields": {
      "submissionPlan": "Start plan",
      "submissionActual": "Start actual",
      "approvalPlan": "End plan",
      "approvalActual": "End actual"
    },
    "regulatoryAuto": "Dates are filled from SMoH / RC registries",
    "ap": {
      "plan": "Planned date",
      "actual": "Actual date"
    }
  },
  "en": {
    "rows": {
      "clinical": "Clinical",
      "regulatory": "Regulatory",
      "marketAccess": "Market Access",
      "release": "Release"
    },
    "miles": {
      "ruSubmission": "RU submission",
      "ruApproval": "RU approval",
      "clinicalStart": "Start",
      "clinicalEnd": "End",
      "maStart": "Start",
      "maEnd": "End",
      "releaseStart": "Start",
      "releaseEnd": "End"
    },
    "fields": {
      "submissionPlan": "Start plan",
      "submissionActual": "Start actual",
      "approvalPlan": "End plan",
      "approvalActual": "End actual"
    },
    "regulatoryAuto": "Dates are filled from SMoH / RC registries",
    "ap": {
      "plan": "Planned date",
      "actual": "Actual date"
    }
  }
}
</i18n>

<style scoped>
.lp-journey {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 10px 12px;
  background: #f7faff;
  border-radius: 12px;
  border: 1px solid rgba(0, 51, 160, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
}
.lp-journey__row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.lp-journey__row--header {
  align-items: flex-end;
  margin-bottom: 2px;
}
.lp-journey__row--editing {
  align-items: center;
}
.lp-journey__row--editing .lp-journey__label {
  padding-top: 0;
}
.lp-journey__label {
  flex: 0 0 100px;
  font-size: 10px;
  font-weight: 800;
  color: #001f63;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 5px;
  padding-top: 4px;
}
.lp-journey__track {
  flex: 1;
  display: flex;
  align-items: stretch;
  min-width: 0;
  width: 100%;
}
.lp-journey__track--header {
  align-items: flex-end;
}
.lp-journey__q-head {
  flex: 1 1 0;
  min-width: 0;
  margin-left: -10px;
  padding: 0 18px 2px 22px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1.1;
  color: #001f63;
  text-align: center;
  white-space: nowrap;
}
.lp-journey__q-head--first {
  margin-left: 0;
  padding-left: 14px;
}
.lp-journey__chevron {
  flex: 1 1 0;
  min-width: 0;
  min-height: 40px;
  padding: 6px 18px 6px 22px;
  margin-left: -10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  clip-path: polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 14px 50%);
  overflow: visible;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
}
.lp-journey__chevron--first {
  margin-left: 0;
  padding-left: 14px;
  clip-path: polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%);
}
.lp-journey__chevron--empty {
  box-shadow: none;
  opacity: 0.75;
}
.lp-journey__empty {
  font-size: 8px;
  font-weight: 600;
  color: #B0B8C4;
  font-style: italic;
  line-height: 1.1;
}
.lp-journey__mile {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 9px;
  line-height: 1.2;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}
.lp-journey__mile > i {
  color: #fff;
  flex-shrink: 0;
}
.lp-journey__mile-label {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
}
.lp-journey__mile-value {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-weight: 800;
  color: #fff;
}
.lp-journey__ap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
  width: 16px;
  min-width: 16px;
  height: 16px;
  padding: 0;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 800;
  line-height: 16px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0;
  text-shadow: none !important;
  border: 1px solid rgba(0, 51, 160, 0.4);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  white-space: nowrap;
  overflow: visible;
  isolation: isolate;
  cursor: help;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.lp-journey__ap:hover {
  transform: scale(1.4);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.22);
}
.lp-journey__mile :deep(.tooltip-host--inline) {
  flex-shrink: 0;
  line-height: 0;
}
.lp-journey__ap--actual {
  border-color: rgba(46, 125, 50, 0.55);
}
.lp-journey__ap--plan {
  border-color: rgba(0, 51, 160, 0.45);
}
.lp-journey__edit {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  min-width: 0;
}
.lp-journey__edit-field {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 600;
  color: #5a6a7a;
  min-width: 0;
}
.lp-journey__edit-field > span {
  flex: 0 0 auto;
  line-height: 1.2;
  white-space: nowrap;
}
.lp-journey__edit-field :deep(.datepicker-wrap) {
  flex: 1 1 auto;
  min-width: 0;
}
.lp-journey__edit-field :deep(.input-group) {
  flex-wrap: nowrap;
  align-items: stretch;
}
.lp-journey__edit-field :deep(.input-group > .input-group-text.dp__input_icon_decorative),
.lp-journey__edit-field :deep(.input-group > .form-control.dp__input) {
  box-sizing: border-box;
  height: 1.875rem;
  min-height: 1.875rem;
  margin: 0;
  padding-top: 0;
  padding-bottom: 0;
}
.lp-journey__edit-field :deep(.input-group > .input-group-text.dp__input_icon_decorative) {
  display: flex;
  align-items: center;
}
.lp-journey__edit-field :deep(.input-group > .form-control.dp__input) {
  line-height: 1.875rem;
}
.lp-journey__hint {
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 1.875rem;
  font-size: 11px;
  color: #7a8e98;
  padding-top: 0;
}
@media (max-width: 900px) {
  .lp-journey__edit {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
